import { ApplicantData } from '@trnsact/trnsact-scorecard-sdk';
import { CreditApplication } from '@trnsact/trnsact-shared-types';
import _ from 'lodash';
import { SelectOption } from 'global';
import { ConditionsFieldsTypes } from '../../../../modules/jsonRulesCriterias/scorecardCriteria/types';

/**
 * Extract FICO score from credit bureau reports
 */
export const getFicoScoreFromCbrs = (cbrs: any[]): number => {
  try {
    const ficoReport = cbrs.find(cbr => cbr && cbr.bureauType === 'FICO');
    if (ficoReport) {
      console.info(`Found FICO Report`);
      const ficoField = ficoReport.fields?.find((f: any) => {
        return f?.key === 'FICO Score';
      });
      if (ficoField && ficoField.value) {
        return parseInt(ficoField.value, 10);
      }
    }
    console.warn('Could not find FICO score... returning score of 0 (not available)');
    return 0;
  } catch (e: any) {
    console.error(`Error getting FICO Score... returning score of 0: ${e.message}`);
    return 0;
  }
};

export interface DnBReportFields {
  failureScore: number | null;
  paydexScore: number | null;
  delinquencyScore: number | null;
  quickRatio: number | null;
  liabToNetWorthPct: number | null;
  yearsInBusiness: number | null;
}

export const getFieldsFromDnBReport = (dnbReport: any): DnBReportFields | null => {
  try {
    if (dnbReport == null) return null;

    const reportType: string | null = _.get(dnbReport, 'reportType', null);
    if (reportType !== 'DUN_AND_BRADSTREET') {
      return null;
    }

    const rawReport: string | null = _.get(dnbReport, 'machineReadableReport', null);
    if (!rawReport) {
      console.warn('DnB report has no machineReadableReport field');
      return null;
    }

    const parsed = JSON.parse(rawReport);

    const applicationValues: any[] = _.get(parsed, 'applicationValues', []);
    const decisionReasons: any[] = _.get(parsed, 'decisionOutcome.automatedDecisionReasons', []);

    const findDecisionValue = (fieldName: string): number | null => {
      const entry = decisionReasons.find((r: any) => r?.fieldName?.toLowerCase() === fieldName.toLowerCase());
      if (!entry || entry.applicationValue == null) return null;
      const parsed = parseFloat(entry.applicationValue);
      return isNaN(parsed) ? null : parsed;
    };

    const findAppValue = (name: string): string | null => {
      const entry = applicationValues.find((v: any) => v?.name === name);
      return entry?.value ?? null;
    };

    const yearsInBusinessRaw = findAppValue('BizProfile-YearsInBusiness');
    const yearsInBusiness = yearsInBusinessRaw != null ? parseFloat(yearsInBusinessRaw) : null;

    return {
      failureScore: findDecisionValue('Failure Score'),
      paydexScore: findDecisionValue('PAYDEX®'),
      delinquencyScore: findDecisionValue('Delinquency Score'),
      quickRatio: findDecisionValue('Quick Ratio'),
      liabToNetWorthPct: findDecisionValue('Total Liabilities to Net Worth (%)'),
      yearsInBusiness: yearsInBusiness != null && !isNaN(yearsInBusiness) ? yearsInBusiness : null,
    };
  } catch (e: any) {
    console.error(`Error extracting fields from DnB report: ${e.message}`);
    return null;
  }
};

/**
 * Extract Credit Line Count
 */
export const getCreditLineCountFromCbrs = (cbrs: any[]): number => {
  try {
    const ficoReport = cbrs.find(cbr => cbr && cbr.bureauType === 'FICO');
    if (ficoReport) {
      console.info(`Found ACCOUNTSNUMBER`);
      const ficoField = ficoReport.fields?.find((f: any) => {
        return f?.key === 'ACCOUNTSNUMBER';
      });
      if (ficoField && ficoField.value) {
        return parseInt(ficoField.value, 10);
      }
    }
    console.warn('Could not find ACCOUNTSNUMBER... returning score of 0 (not available)');
    return 0;
  } catch (e: any) {
    console.error(`Error getting ACCOUNTSNUMBER... returning score of 0: ${e.message}`);
    return 0;
  }
};

/**
 * Build ApplicantData from credit application and equipment data
 */
export const buildApplicantData = (
  creditApplication: CreditApplication,
  equipmentData: any,
  vo: any,
  cs: any
): ApplicantData => {
  let ficoScore = 0;
  let dbnReportFields = null;
  let creditLineCount = 0;
  const noPgPresent = _.isEmpty(_.get(creditApplication, 'creditApplicationOwner[0].personalGuarantor'));
  const dnbReportSource = _.get(creditApplication, 'mostRecentCreditReportBusiness', null);
  const ficoScoreSource = _.get(creditApplication, 'creditApplicationOwner[0].personalGuarantor.mostRecentCbrs', []);
  if (!_.isEmpty(ficoScoreSource)) {
    ficoScore = getFicoScoreFromCbrs(ficoScoreSource);
    creditLineCount = getCreditLineCountFromCbrs(ficoScoreSource);
  }
  if (!_.isEmpty(dnbReportSource)) {
    dbnReportFields = getFieldsFromDnBReport(dnbReportSource);
  }

  let eqModelYear = undefined;
  let eqAge = undefined;
  const currentYear = new Date().getFullYear();

  if (!_.isEmpty(_.get(equipmentData, 'equipments[0].year'))) {
    eqModelYear = Number(_.get(equipmentData, 'equipments[0].year'));
    eqAge = currentYear - eqModelYear;
  }

  const equipment = _.get(equipmentData, 'equipments[0]', {});

  const timeInBusinessMonths = creditApplication.yearsInBusiness ? creditApplication.yearsInBusiness * 12 : undefined;
  const timeWithCdlMonths = _.get(creditApplication, 'primaryContact.yearsWithCdl', undefined)
    ? _.get(creditApplication, 'primaryContact.yearsWithCdl', 0) * 12
    : undefined;
  const bankruptcyDate = _.get(creditApplication, 'bankruptcyDate', undefined);
  const priorBankruptcy = _.get(creditApplication, 'priorBankruptcy', undefined) === 'yes';

  const timeSinceBankruptcyMonths = bankruptcyDate
    ? Math.floor((new Date().getTime() - new Date(bankruptcyDate).getTime()) / (1000 * 60 * 60 * 24 * 30))
    : undefined;

  // Calculate bankruptcy filling years as a categorical string
  let bankruptcyFillingYears: string;
  if (!priorBankruptcy || !bankruptcyDate) {
    bankruptcyFillingYears = 'Never';
  } else {
    const timeSinceBankruptcyYears = timeSinceBankruptcyMonths ? Math.floor(timeSinceBankruptcyMonths / 12) : 0;
    bankruptcyFillingYears = timeSinceBankruptcyYears > 5 ? '>5 Years' : '<5 Years';
  }

  const calculatedYearsBusinessStablished = creditApplication.dateEstablished
    ? Math.floor(
        (new Date().getTime() - new Date(creditApplication.dateEstablished).getTime()) / (1000 * 60 * 60 * 24 * 365)
      )
    : undefined;
  const calculateMonthsBusinessStablished = calculatedYearsBusinessStablished
    ? calculatedYearsBusinessStablished * 12
    : undefined;

  let applicant: ApplicantData = {
    // === NUMERIC FIELDS (from FACT_NAMES_NUMERIC) ===
    DEAL_SIZE: creditApplication.amountRequested,
    TIME_IN_BUSINESS_MONTHS: timeInBusinessMonths,
    FICO: ficoScore,
    NUMBER_OF_TRUCKS: creditApplication.piecesOfTruckEqpt || creditApplication.trucksOwned,
    EQUIPMENT_YEAR: eqModelYear,
    EQUIPMENT_AGE_YEARS: eqAge,
    EQUIPMENT_HOURS: equipment.hours,
    DOWN_PAYMENT: vo?.downPayment,
    TIME_WITH_CDL_MONTHS: timeWithCdlMonths,
    TIME_SINCE_BANKRUPTCY_MONTHS: timeSinceBankruptcyMonths,
    ODOMETER_MILEAGE: equipment.mileage,
    COVERAGE_TERM_LENGTH_MONTHS: undefined,
    IN_SERVICE_DATE: undefined,
    YEARS_IN_BUSINESS: creditApplication.yearsInBusiness,
    BANK_BALANCE_PER_TRUCK: undefined,
    PERSONAL_CREDIT_SCORE: ficoScore,
    INDUSTRY_EXPERIENCE_YEARS: creditApplication.industryExpYears,
    AVERAGE_BANK_BALANCE:
      _.get(creditApplication, 'primaryBankAverageBalance', undefined) ||
      _.get(creditApplication, 'averageDailyBalance', undefined),
    FLEET_SIZE: creditApplication.piecesOfTruckEqpt || creditApplication.trucksOwned,
    NUMBER_OF_TRADE_LINES: creditLineCount,
    BANKRUPTCY_FILLING_YEARS: bankruptcyFillingYears,
    NUMBER_OF_EMPLOYEES: creditApplication.numberOfEmployees,

    // === STRING FIELDS (from FACT_NAMES_STRING) ===
    EQUIPMENT_TYPE: creditApplication.equipmentCategoryCode,
    EQUIPMENT_MAKE: equipment.make,
    EQUIPMENT_MODEL: equipment.model,
    EQUIPMENT_CLASS: equipment.classType,
    FINANCING_TYPE: vo?.financingType,
    LEGAL_STRUCTURE: creditApplication.legalStructure,
    EQUIPMENT_CONDITION: equipment.newUsed,
    USA_STATE: creditApplication.state,
    CANADA_REGION: creditApplication.state,
    VIN: equipment.serialNumberVin,
    DEDUCTIBLE: undefined,
    COVERAGE_TERM: undefined,
    CONTRACT_TERM: undefined,
    BILLING_FREQUENCY: undefined,
    AFTERMARKET_PRODUCT_SOURCE: undefined,
    LOCATION_ID: creditApplication.location?.locationId,
    AFTERMARKET_PRODUCT_ID: undefined,
    AFTERMARKET_PRODUCT_ID_EXTERNAL: undefined,
    AFTERMARKET_PRODUCT_IDS_EXTERNAL_QUALIFIED: undefined,
    TERM_AND_MILEAGE: undefined,
    EQUIPMENT_ENGINE_TYPE: undefined,
    ENGINE_TORQUE_MODE: undefined,
    DEALERS: undefined,
    COMPARABLE_LOAN_HISTORY: undefined,
    DRIVER_STRUCTURE: creditApplication.numOfOwnerOps ? 'OWNER_OPERATOR' : undefined,
    HOME_OWNERSHIP: _.get(creditApplication, 'primaryContact.homeType', undefined),

    // === BOOLEAN FIELDS (from FACT_NAMES_BOOLEAN) ===
    OWNER_OPERATOR: creditApplication.numOfOwnerOps && creditApplication.numOfOwnerOps > 0,
    LONG_HAUL: creditApplication.longHaulRegional === 'Long Haul',
    SOLE_PROPRIETORSHIP: creditApplication.legalStructure === 'SOLE_PROPRIETORSHIP',
    FIRST_TIME_BUYER: _.get(creditApplication, 'primaryContact.firstTimeFinancing', false),
    CORPORATION_ONLY: creditApplication.legalStructure === 'CORPORATION',
    PRIOR_FINANCING_EXPERIENCE: creditApplication.previousFinanceExp === 'true',
    US_CITIZEN: _.get(creditApplication, 'primaryContact.usCitizen', undefined),
    EQUIPMENT_HAS_ATS: undefined,
    IS_REPEAT_BORROWER: creditApplication.previousFinanceExp === 'true',

    // === DATE FIELDS (from FACT_NAMES_DATE) ===
    DATE_VALID: creditApplication.dateEstablished || creditApplication.dateIncorporated,

    //Passed fields not used in scoring but needed for record keeping
    NO_PG_PRESENT: noPgPresent,
  };

  // Calculate BANK_BALANCE_PER_TRUCK if we have both values
  if (applicant.AVERAGE_BANK_BALANCE && applicant.NUMBER_OF_TRUCKS && applicant.NUMBER_OF_TRUCKS > 0) {
    applicant.BANK_BALANCE_PER_TRUCK = applicant.AVERAGE_BANK_BALANCE / applicant.NUMBER_OF_TRUCKS;
  }

  // If there's existing scorecard result data, use it as base (allows manual overrides)
  if (cs && cs.scoreCardResult) {
    applicant = cs.scoreCardResult.runtimeInput;
  } else {
    if (vo && vo.scoreCardResult) {
      applicant = vo.scoreCardResult.runtimeInput;
    }
  }

  // Add FICO score if available
  if (ficoScore) {
    applicant = { ...applicant, PERSONAL_CREDIT_SCORE: ficoScore, FICO: ficoScore };
  }

  // Add DnB report fields if available
  if (dbnReportFields) {
    applicant = {
      ...applicant,
      DNB_FAILURE_SCORE: dbnReportFields.failureScore,
      DNB_PAYDEX_SCORE: dbnReportFields.paydexScore,
      DNB_DELINQUENCY_SCORE: dbnReportFields.delinquencyScore,
      DNB_QUICK_RATIO: dbnReportFields.quickRatio,
      DNB_LIAB_TO_NET_WORTH_PCT: dbnReportFields.liabToNetWorthPct,
      DNB_YEARS_IN_BUSINESS: dbnReportFields.yearsInBusiness,
    };
  }

  return applicant;
};

/**
 * Transform applicant data to handle SelectOption objects for scoring
 */
export const transformApplicantForScoring = (
  applicantData: ApplicantData,
  manualFields: { [key: string]: any },
  listOfFacts: Map<
    string,
    {
      fact: string;
      type: ConditionsFieldsTypes;
      factType: any;
    }
  >
): any => {
  const mergedApplicant = { ...applicantData, ...manualFields };

  return Object.entries(mergedApplicant).reduce((acc, [key, value]) => {
    const factInfo = listOfFacts.get(key);

    if (factInfo?.type === ConditionsFieldsTypes.String) {
      if (value && typeof value === 'object' && 'value' in value) {
        acc[key] = value.value;
      } else if (Array.isArray(value)) {
        const firstValue = value[0];
        acc[key] =
          firstValue && typeof firstValue === 'object' && 'value' in firstValue ? firstValue.value : firstValue;
      } else {
        acc[key] = value;
      }
    } else {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
};

/**
 * Extract string options from scorecard criteria
 */
export const extractStringOptions = (scorecardCriteria: any): Map<string, SelectOption[]> => {
  const stringOptionsMap = new Map<string, Set<string>>();

  scorecardCriteria.jsonCriteria.scoreCriteria.forEach((scoreCardRow: any) => {
    scoreCardRow?.jsonRules.conditions.all.forEach((cond: any) => {
      const factName = cond.fact;

      if (cond.value) {
        const values = Array.isArray(cond.value) ? cond.value : [cond.value];
        values.forEach((v: any) => {
          if (v && typeof v === 'string') {
            if (!stringOptionsMap.has(factName)) {
              stringOptionsMap.set(factName, new Set());
            }
            stringOptionsMap.get(factName)!.add(v);
          }
        });
      }
    });

    if (scoreCardRow && scoreCardRow?.customCriteria) {
      Object.values(scoreCardRow?.customCriteria).forEach((criteria: any) => {
        if (criteria.type === 'string' && criteria.value) {
          if (!stringOptionsMap.has(criteria.name)) {
            stringOptionsMap.set(criteria.name, new Set());
          }
          const values = Array.isArray(criteria.value) ? criteria.value : [criteria.value];
          values.forEach((v: any) => {
            if (v && typeof v === 'string') {
              stringOptionsMap.get(criteria.name)!.add(v);
            }
          });
        }
      });
    }
  });

  const selectOptionsMap = new Map<string, SelectOption[]>();
  stringOptionsMap.forEach((valuesSet, factName) => {
    const options = Array.from(valuesSet)
      .sort()
      .map(value => ({ value, label: value }));
    selectOptionsMap.set(factName, options);
  });

  return selectOptionsMap;
};
